/**
 * WordPress dependencies
 */
import {
	Button,
	Icon,
	VisuallyHidden,
	__experimentalHStack as HStack,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { useEntityRecords, store as coreStore } from '@wordpress/core-data';
import { useDispatch } from '@wordpress/data';
import { addQueryArgs } from '@wordpress/url';
import { decodeEntities } from '@wordpress/html-entities';
import { useState, useMemo, Fragment, RawHTML } from '@wordpress/element';
import { dateI18n, getDate, getSettings } from '@wordpress/date';
import { DataViews, filterSortAndPaginate } from '@wordpress/dataviews/wp';
import { parse } from '@wordpress/block-serialization-default-parser';
import { datesField } from './fields.js';
import { renamePost } from '@wordpress/fields';
import {
	editPost,
	duplicatePost,
	trashPost,
	displayRules,
	permanentlyDeletePost,
	restorePost,
	viewPostRevisions,
	startStop,
	StartStopButton,
} from './actions/index.js';

import {
	mobile,
	tablet,
	desktop,
	check,
	closeSmall,
	warning,
} from '@wordpress/icons';
import { RulesModal } from '../../modal-conditions/modal';

import './style.scss';

/**
 * Internal dependencies
 */
import { Header } from '../components/header';
const EMPTY_ARRAY = [];
const STATUSES = [
	{ value: 'draft', label: __( 'Draft' ) },
	{ value: 'future', label: __( 'Scheduled' ) },
	{ value: 'pending', label: __( 'Pending Review' ) },
	{ value: 'private', label: __( 'Private' ) },
	{ value: 'publish', label: __( 'Published' ) },
	{ value: 'trash', label: __( 'Trash' ) },
];
const DEFAULT_STATUSES = 'draft,future,pending,private,publish'; // All but 'trash'.
const defaultLayouts = {
	table: {
		layout: {
			primaryField: 'id',
			combinedFields: [
				{
					id: 'title',
					label: 'Title',
					children: [ 'excerpt' ],
					direction: 'vertical',
				},
			],
			styles: {
				running: {
					maxWidth: '100px',
					width: '100px',
				},
			},
		},
	},
};

export const Dashboard = () => {
	const [ showModal, setShowModal ] = useState( false );
	const [ view, setView ] = useState( {
		type: 'table',
		perPage: 10,
		page: 1,
		sort: {
			field: 'date',
			direction: 'desc',
		},
		search: '',
		filters: [],
		titleField: 'title',
		descriptionField: 'excerpt',
		fields: [
			'running',
			'triggers',
			'locations',
			'exclude',
			'devices',
			'users',
			'dates',
		],
		layout: defaultLayouts.table.layout,
	} );

	const queryArgs = useMemo( () => {
		const filters = {};
		view.filters.forEach( ( filter ) => {
			if ( filter.field === 'status' && filter.operator === 'isAny' ) {
				filters.status = filter.value;
			}
			if ( filter.field === 'author' && filter.operator === 'is' ) {
				filters.author = filter.value;
			}
		} );
		// We want to provide a different default item for the status filter
		// than the REST API provides.
		if ( ! filters.status || filters.status === '' ) {
			filters.status = DEFAULT_STATUSES;
		}
		return {
			per_page: -1,
			page: view.page,
			_embed: 'author',
			order: view.sort?.direction,
			orderby: view.sort?.field,
			search: view.search,
			...filters,
		};
	}, [ view ] );

	const { records: popups, isResolving: isLoadingPopups } = useEntityRecords(
		'postType',
		'popper',
		queryArgs
	);

	const { records: authors, isResolving: isLoadingAuthors } =
		useEntityRecords( 'root', 'user', { per_page: -1 } );

	const fields = useMemo(
		() => [
			{
				header: (
					<VisuallyHidden>
						{ __( 'Running', 'popper' ) }
					</VisuallyHidden>
				),
				id: 'running',
				render: ( { item } ) => {
					return (
						<StartStopButton
							icon={
								item.meta.popper_rules?.running
									? 'controls-play'
									: 'controls-pause'
							}
							iconSize="15"
							style={ {
								color: item.meta.popper_rules?.running
									? 'green'
									: '',
							} }
							text={
								item.meta.popper_rules?.running
									? __( 'Running', 'popper' )
									: __( 'Paused', 'popper' )
							}
							size="compact"
							items={ [ item ] }
							label={ __(
								'Click to toggle running status',
								'popper'
							) }
						/>
					);
				},
				enableHiding: false,
			},
			{
				header: __( 'Title' ),
				id: 'title',
				getValue: ( { item } ) => item.title.rendered,
				render: ( { item } ) => {
					return decodeEntities(
						item.title?.rendered || 'Popup #' + item.id
					);
				},
				enableGlobalSearch: true,
				enableHiding: false,
				enableSorting: true,
			},
			{
				header: __( 'Excerpt' ),
				id: 'excerpt',
				getValue: ( { item } ) => item.excerpt.raw,
			},
			{
				header: __( 'Triggers' ),
				id: 'triggers',
				getValue: ( item ) => item.content.raw,
				render: ( { item } ) => {
					const block = parse( item.content.raw );
					if ( ! block.length ) {
						return null;
					}

					const triggers = block[ 0 ].attrs?.triggers?.map( ( o ) => {
						const result = o.replace( /([A-Z])/g, ' $1' );
						const finalResult =
							result.charAt( 0 ).toUpperCase() +
							result.slice( 1 );

						return finalResult;
					} );
					return (
						<RawHTML>
							{ triggers?.map( ( o ) => {
								return `${ o }` + '<br />';
							} ) }
						</RawHTML>
					);
				},
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Locations' ),
				id: 'locations',
				getValue: ( item ) => item.meta.popper_rules?.locations,
				render: ( { item } ) => {
					const locations = item.popper_locations.include.map(
						( loc ) => loc.rule !== ''
					);
					if (
						item.meta.popper_rules?.running &&
						! locations.length
					) {
						return (
							<HStack justify={ 'flex-start' } expanded={ false }>
								<Icon icon={ warning } />
								<Button
									onClick={ ( e ) => {
										e.stopPropagation();
										setShowModal( item.id );
									} }
									variant="link"
								>
									{ __( 'Please add a location', 'popper' ) }
								</Button>
							</HStack>
						);
					}
					return (
						<RawHTML>
							{ item.popper_locations.include.join( '<br /> ' ) }
						</RawHTML>
					);
				},
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Exclude' ),
				id: 'esclude',
				getValue: ( item ) => item.meta.popper_rules?.esclude,
				render: ( { item } ) => {
					return (
						<RawHTML>
							{ item.popper_locations.exclude.join( '<br /> ' ) }
						</RawHTML>
					);
				},
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Devices' ),
				id: 'devices',
				getValue: ( item ) => item.meta.popper_rules?.device,
				render: ( { item } ) => {
					if (
						item.meta.popper_rules?.running &&
						! item.meta.popper_rules?.device.length
					) {
						return (
							<HStack justify={ 'flex-start' } expanded={ false }>
								<Icon icon={ warning } />
								<Button
									onClick={ ( e ) => {
										e.stopPropagation();
										setShowModal( item.id );
									} }
									variant="link"
								>
									{ __(
										'Please add at least one device',
										'popper'
									) }
								</Button>
							</HStack>
						);
					}
					return (
						<span>
							{ item.meta.popper_rules?.device.includes(
								'mobile'
							) ? (
								<Icon icon={ mobile } />
							) : null }
							{ item.meta.popper_rules?.device.includes(
								'tablet'
							) ? (
								<Icon icon={ tablet } />
							) : null }
							{ item.meta.popper_rules?.device.includes(
								'desktop'
							) ? (
								<Icon icon={ desktop } />
							) : null }
						</span>
					);
				},
				enableHiding: true,
				enableSorting: false,
			},
			{
				header: __( 'Users' ),
				id: 'users',
				getValue: ( item ) => item.meta.popper_rules?.user,
				render: ( { item } ) => {
					return <RawHTML>{ item.popper_locations.user }</RawHTML>;
				},
				enableHiding: true,
				enableSorting: false,
			},
			datesField,
			{
				header: __( 'Author' ),
				id: 'author',
				getValue: ( { item } ) => item._embedded?.author[ 0 ]?.name,
				elements:
					authors?.map( ( { id, name } ) => ( {
						value: id,
						label: name,
					} ) ) || [],
			},
			{
				header: __( 'Status' ),
				id: 'status',
				elements: STATUSES,
				filterBy: {
					operators: [ 'isAny', 'isNone', 'isAll', 'isNotAll' ],
				},
			},
			{
				header: __( 'Date' ),
				id: 'date',
				render: ( { item } ) => {
					const formattedDate = dateI18n(
						getSettings().formats.datetimeAbbreviated,
						getDate( item.date )
					);
					return <time>{ formattedDate }</time>;
				},
			},
			{
				id: 'pattern',
				header: __( 'Pattern' ),
				getValue: ( item ) => item.meta._popper_pattern,
				maxWidth: 20,
				render: ( { item } ) => {
					return item.meta._popper_pattern ? (
						<Icon icon={ check } />
					) : (
						<Icon icon={ closeSmall } />
					);
				},
				enableHiding: true,
				enableSorting: false,
			},
		],
		[ authors ]
	);

	const { data: shownData, paginationInfo } = useMemo( () => {
		return filterSortAndPaginate( popups, view, fields );
	}, [ view, popups, fields ] );

	renamePost.isEligible = () => true;

	const actions = useMemo(
		() => [
			restorePost,
			displayRules,
			startStop,
			editPost,
			duplicatePost,
			trashPost,
			permanentlyDeletePost,
			viewPostRevisions,
			renamePost,
		],
		[]
	);

	const { saveEditedEntityRecord } = useDispatch( coreStore );
	const closeModal = () => {
		saveEditedEntityRecord( 'postType', 'popper', showModal );
		setShowModal( false );
	};

	return (
		<Fragment>
			<Header title={ __( 'Popups', 'popper' ) }>
				<Button
					text={ __( 'Add new' ) }
					variant="primary"
					size="small"
					href={ 'post-new.php?post_type=popper' }
				/>
			</Header>
			<div className="popper-content">
				<DataViews
					paginationInfo={ paginationInfo }
					fields={ fields }
					actions={ actions }
					data={ shownData || EMPTY_ARRAY }
					isLoading={ isLoadingPopups || isLoadingAuthors }
					view={ view }
					onChangeView={ setView }
					defaultLayouts={ defaultLayouts }
					onClickItem={ ( item ) => {
						const href = addQueryArgs( 'post.php', {
							post: item.id,
							action: 'edit',
						} );
						document.location.href = href;
					} }
				/>
				{ showModal && (
					<RulesModal
						postId={ showModal }
						onRequestClose={ closeModal }
					/>
				) }
			</div>
		</Fragment>
	);
};
